import { useParams, Link } from "react-router-dom";
import { useMemo } from "react";
import SEO from "@/components/base/SEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);
  const categoryArticles = useMemo(
    () => articles.filter((a) => a.category === slug),
    [slug]
  );

  if (!category) {
    return (
      <div className="min-h-screen bg-background-950 flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-200 mb-2">分类不存在</h1>
            <Link to="/" className="text-primary-400 hover:text-primary-300 transition-colors cursor-pointer text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category.name} - 黑料王`,
    description: category.description,
    url: `https://heiliaowang.com/category/${category.slug}`,
    isPartOf: {
      "@type": "WebSite",
      name: "黑料王",
      url: "https://heiliaowang.com",
    },
  };

  return (
    <>
      <SEO
        title={`${category.name} - 黑料王`}
        description={`${category.description}，黑料王为您带来最全${category.name}资讯，每日更新独家内幕。`}
        keywords={`${category.name},娱乐圈黑料,明星${category.name},黑料王`}
        canonicalUrl={`https://heiliaowang.com/category/${category.slug}`}
        schema={categorySchema}
      />

      <div className="min-h-screen bg-background-950 flex flex-col">
        <Navbar />
        <main className="flex-1">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: category.name },
              ]}
            />

            <div className="py-4 md:py-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/10">
                  <i className={`${category.icon} text-lg text-primary-400`}></i>
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-100">
                    {category.name}
                  </h1>
                  <p className="text-sm text-foreground-500">{category.description}</p>
                </div>
              </div>
              <p className="text-xs text-foreground-600 mt-1">
                共 {categoryArticles.length} 篇相关爆料
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-xs font-medium rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? "bg-primary-500/20 text-primary-400 border border-primary-500/30"
                      : "bg-background-900 text-foreground-500 hover:text-foreground-200 border border-background-800/60 hover:border-background-700"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 pb-10">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} variant="default" />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-800 mx-auto mb-4">
                  <i className="ri-inbox-line text-2xl text-foreground-600"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>

      <script type="application/ld+json">{JSON.stringify(categorySchema)}</script>
    </>
  );
}